import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset } from '../api/account';
import { validatePassword } from '../auth/validation';
import { formatResetConfirmError } from './resetConfirmError';

/// Reset-link landing page. The server emails a URL of the form
/// `${PANEL_URL}/reset?token=…` — we read that token from the query
/// string, prompt for a new password (twice, to catch typos), and POST
/// to `/account/password-reset/confirm`. On success we send the user
/// back to /login with a banner.
export default function ResetConfirm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // If the email got mangled and there's no token at all, show a useful
  // error immediately rather than letting the user fill out the form and
  // get a generic 400 from the server.
  if (!token) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-header">
            <h1>Invalid reset link</h1>
            <p>This link is missing its token. Request a new one to try again.</p>
          </div>
          <Link to="/forgot-password" className="btn btn-primary btn-link-as-button">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    const passwordCheck = validatePassword(password, 'register');
    if (!passwordCheck.ok) {
      setError(passwordCheck.message);
      return;
    }
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(token, password);
      // Success: bounce to login with a banner. We don't auto-sign-in —
      // the password change just invalidated every existing refresh
      // token (server-side), so a re-login is required anyway.
      navigate('/login?reset=success', { replace: true });
    } catch (err) {
      setError(formatResetConfirmError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <h1>Choose a new password</h1>
          <p>You'll be signed out of every device.</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="password">New password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 12 characters"
              required
              autoFocus
              autoComplete="new-password"
              minLength={12}
              disabled={loading}
            />
            <p className="hint">At least 12 characters.</p>
          </div>
          <div className="form-group">
            <label htmlFor="confirm">Confirm new password</label>
            <input
              id="confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter your new password"
              required
              autoComplete="new-password"
              minLength={12}
              disabled={loading}
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving…' : 'Set new password'}
          </button>
        </form>

        <div className="auth-link-row-spacious">
          <Link to="/login" className="btn-link">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
